export const PENDING_CHECKOUT_KEY = "pending_checkout_payment";
export const CHECKOUT_RECOVERY_KEY = "checkout_recovery_context";

function normalizeCheckoutReturnFlow(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "order_settlement") return "order_settlement";
  return "checkout";
}

function normalizeReturnPath(value) {
  const normalized = String(value || "").trim();
  if (!normalized || !normalized.startsWith("/") || normalized.startsWith("//")) {
    return null;
  }

  return normalized;
}

function readStorageJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStorageJson(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function removeStorageKey(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

export function normalizeRecoveryOrderId(value) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
}

function normalizeCheckoutRecoveryRecord(value) {
  if (!value || typeof value !== "object") return null;

  const orderId = normalizeRecoveryOrderId(value.orderId ?? value.order_id);
  if (!orderId) return null;

  const checkoutSessionId = String(
    value.checkoutSessionId ?? value.checkout_session_id ?? ""
  ).trim() || null;
  const clientRequestId = String(
    value.clientRequestId ?? value.client_request_id ?? ""
  ).trim() || null;
  const createdAt = String(value.createdAt ?? "").trim() || null;
  const flow = normalizeCheckoutReturnFlow(
    value.flow ?? value.returnFlow ?? value.return_flow ?? value.originFlow
  );
  const returnPath = normalizeReturnPath(
    value.returnPath ?? value.return_path ?? null
  );

  return {
    orderId,
    checkoutSessionId,
    clientRequestId,
    createdAt,
    flow,
    returnPath,
  };
}

function normalizeHostedCheckoutPaymentState(value) {
  if (!value || typeof value !== "object") return null;

  const rawCanSettle = value.canSettle ?? value.can_settle;
  const state = String(value.state || "").trim().toUpperCase() || null;
  const sessionStatus = String(
    value.sessionStatus ?? value.session_status ?? ""
  ).trim().toUpperCase() || null;
  const latestPaymentStatus = String(
    value.latestPaymentStatus ?? value.latest_payment_status ?? ""
  ).trim().toUpperCase() || null;

  return {
    state,
    canSettle: rawCanSettle == null ? null : Boolean(rawCanSettle),
    sessionStatus,
    latestPaymentStatus,
  };
}

export function getHostedCheckoutRetryState(order) {
  const paymentMethod = String(order?.payment_method || "").trim().toUpperCase();
  const orderStatus = String(order?.order_status || order?.status || "").trim().toUpperCase();
  const paymentStatus = String(order?.payment_status || "").trim().toUpperCase();
  const checkoutPayment = normalizeHostedCheckoutPaymentState(
    order?.checkout_payment || order?.checkoutPayment
  );
  const isHostedCheckout = paymentMethod === "PAYMONGO_CHECKOUT" || Boolean(checkoutPayment);

  if (!isHostedCheckout) {
    return {
      canRetry: false,
      phase: "not_hosted_checkout",
      checkoutPayment,
    };
  }

  if (checkoutPayment?.state === "AWAITING_PAYMENT") {
    const canRetry = checkoutPayment.canSettle ?? true;

    return {
      canRetry,
      phase: canRetry ? "recoverable" : "awaiting_payment_locked",
      checkoutPayment,
    };
  }

  if (checkoutPayment?.state === "PAID") {
    return {
      canRetry: false,
      phase: "paid",
      checkoutPayment,
    };
  }

  if (checkoutPayment?.state === "EXPIRED") {
    return {
      canRetry: false,
      phase: "expired_terminal",
      checkoutPayment,
    };
  }

  if (checkoutPayment?.state === "FAILED") {
    return {
      canRetry: false,
      phase: "failed_terminal",
      checkoutPayment,
    };
  }

  if (checkoutPayment?.state === "CANCELLED") {
    return {
      canRetry: false,
      phase: "cancelled_terminal",
      checkoutPayment,
    };
  }

  if (paymentStatus === "PAID" || orderStatus === "PAID") {
    return {
      canRetry: false,
      phase: "paid",
      checkoutPayment,
    };
  }

  if (orderStatus === "CANCELLED") {
    return {
      canRetry: false,
      phase: "cancelled_terminal",
      checkoutPayment,
    };
  }

  return {
    canRetry: false,
    phase: "unknown",
    checkoutPayment,
  };
}

export function getHostedCheckoutRetryBlockMessage(order) {
  const retryState = getHostedCheckoutRetryState(order);

  if (retryState.canRetry || retryState.phase === "paid") {
    return null;
  }

  if (retryState.phase === "expired_terminal") {
    return "This PayMongo payment window already closed. Open My Purchase to review the updated order status.";
  }

  if (["failed_terminal", "cancelled_terminal"].includes(retryState.phase)) {
    return "This order can no longer reopen PayMongo checkout. Open My Purchase to review the updated order status.";
  }

  return null;
}

function normalizeRecoveredItem(item, fallbackBusiness = {}) {
  const quantity = Number(item?.quantity || 0);
  const price = Number(item?.price || 0);

  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(price) || price <= 0) return null;

  const productId = normalizeRecoveryOrderId(item?.productId ?? item?.product_id);
  const variantId = normalizeRecoveryOrderId(item?.variantId ?? item?.variant_id);
  const businessId = normalizeRecoveryOrderId(
    item?.businessId
    ?? item?.business_id
    ?? fallbackBusiness.businessId
    ?? fallbackBusiness.business_id
  );
  const businessName =
    item?.businessName
    || item?.business_name
    || fallbackBusiness.businessName
    || fallbackBusiness.business_name
    || "Seller";

  return {
    id: item?.id ?? productId ?? null,
    productId: productId ?? item?.id ?? null,
    variantId: variantId ?? null,
    businessId,
    businessName,
    quantity,
    price,
    name: item?.name || item?.product_name || "Product",
    image: item?.image || null,
    variation: item?.variation || null,
    isSelected: true,
    key: `recovery-${businessId || "seller"}-${item?.id ?? productId ?? "item"}-${variantId || "base"}`,
  };
}

function normalizeRecoveredAddress(deliveryAddress = {}, addressId = null) {
  return {
    id: addressId,
    full_name: deliveryAddress.full_name || "",
    phone: deliveryAddress.phone || "",
    address_line1: deliveryAddress.street_address || "",
    address_line2: deliveryAddress.address_line2 || "",
    landmark: deliveryAddress.landmark || "",
    city: deliveryAddress.city || deliveryAddress.city_municipality || "",
    province: deliveryAddress.province || "",
    barangay: deliveryAddress.barangay || "",
    postal_code: deliveryAddress.postal_code || "",
    region: deliveryAddress.region || "",
    label: deliveryAddress.label || "",
    is_default: false,
  };
}

export function readPendingCheckoutPayment() {
  return normalizeCheckoutRecoveryRecord(readStorageJson(PENDING_CHECKOUT_KEY));
}

export function writePendingCheckoutPayment(value) {
  const normalized = normalizeCheckoutRecoveryRecord(value);
  if (!normalized) return false;
  return writeStorageJson(PENDING_CHECKOUT_KEY, normalized);
}

export function clearPendingCheckoutPayment() {
  removeStorageKey(PENDING_CHECKOUT_KEY);
}

export function readCheckoutRecoveryContext() {
  return normalizeCheckoutRecoveryRecord(readStorageJson(CHECKOUT_RECOVERY_KEY));
}

export function writeCheckoutRecoveryContext(value) {
  const normalized = normalizeCheckoutRecoveryRecord(value);
  if (!normalized) return false;
  return writeStorageJson(CHECKOUT_RECOVERY_KEY, normalized);
}

export function clearCheckoutRecoveryContext() {
  removeStorageKey(CHECKOUT_RECOVERY_KEY);
}

export function persistCheckoutRecoveryContext(value) {
  const normalized = normalizeCheckoutRecoveryRecord(value);
  if (!normalized) return false;

  const pendingSaved = writePendingCheckoutPayment(normalized);
  const recoverySaved = writeCheckoutRecoveryContext(normalized);
  return pendingSaved || recoverySaved;
}

export function buildCheckoutRecoveryDraft(order) {
  const orderId = normalizeRecoveryOrderId(order?.id);
  if (!orderId) return null;

  const subOrders = Array.isArray(order?.sub_orders) ? order.sub_orders : [];
  const items = subOrders.length > 0
    ? subOrders.flatMap((subOrder) => {
      const subOrderItems = Array.isArray(subOrder?.items) ? subOrder.items : [];
      return subOrderItems
        .map((item) => normalizeRecoveredItem(item, subOrder))
        .filter(Boolean);
    })
    : (Array.isArray(order?.items) ? order.items : [])
      .map((item) => normalizeRecoveredItem(item))
      .filter(Boolean);

  const sellerShipping = {};
  const sellerNotes = {};
  const sellerDeliveryFees = {};
  subOrders.forEach((subOrder) => {
    const businessName = subOrder?.businessName || subOrder?.business_name || "Seller";
    sellerShipping[businessName] = subOrder?.shipping_method || "standard";
    sellerNotes[businessName] = subOrder?.customer_note || "";
    sellerDeliveryFees[businessName] = Number(subOrder?.delivery_fee || 0);
  });

  return {
    orderId,
    paymentMethod: String(order?.payment_method || "PAYMONGO_CHECKOUT").toUpperCase(),
    paymentStatus: String(order?.payment_status || "PENDING").toUpperCase(),
    orderStatus: String(order?.order_status || order?.status || "PENDING").toUpperCase(),
    addressId: normalizeRecoveryOrderId(order?.address_id),
    addressSnapshot: normalizeRecoveredAddress(order?.delivery_address || {}, order?.address_id ?? null),
    items,
    sellerShipping,
    sellerNotes,
    sellerDeliveryFees,
  };
}

export function resolveCheckoutRecoveryOrderId({
  requestedOrderId = null,
  storedRecovery = null,
  cartItemCount = 0,
  cartLoading = false,
}) {
  const normalizedRequestedOrderId = normalizeRecoveryOrderId(requestedOrderId);
  if (normalizedRequestedOrderId) return normalizedRequestedOrderId;

  if (cartLoading) return null;
  if (Number(cartItemCount || 0) > 0) return null;
  if (normalizeCheckoutReturnFlow(storedRecovery?.flow) !== "checkout") return null;

  return normalizeRecoveryOrderId(storedRecovery?.orderId);
}
